import { useCallback, useEffect, useRef, useState } from "react";
import { ClipUnavailableError, loadClip } from "../audio/clip.ts";
import { createClipPlayer, type ClipPlayer } from "../audio/player.ts";

export type ClipFailure = { kind: "quota" | "other"; message: string };

/**
 * Owns the AudioContext and the decoded Clip for whichever Track is active.
 *
 * The fetch/decode happens once per Track, so the signed url's 15-minute life
 * cannot interrupt a Round in progress (ADR 0001), and playback is scheduled
 * sample-accurately from the decoded buffer (ADR 0002).
 */
export function useClip(trackId: number | null, volume = 1) {
  const playerRef = useRef<ClipPlayer | null>(null);
  const [buffer, setBuffer] = useState<AudioBuffer | null>(null);
  const [failure, setFailure] = useState<ClipFailure | null>(null);
  // Bumping this re-runs the load. A failed Slot must be recoverable: the Worker
  // being briefly unreachable should not strand a Daily Set — and therefore a
  // Streak — with no way forward.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (trackId === null) {
      setBuffer(null);
      return;
    }

    let cancelled = false;
    setBuffer(null);
    setFailure(null);

    void (async () => {
      try {
        playerRef.current ??= createClipPlayer();
        const { buffer: decoded } = await loadClip(trackId, {
          decode: (bytes) => playerRef.current!.context.decodeAudioData(bytes),
        });
        if (!cancelled) setBuffer(decoded);
      } catch (err) {
        if (cancelled) return;
        const quota = err instanceof ClipUnavailableError && err.kind === "quota";
        setFailure({
          kind: quota ? "quota" : "other",
          message: quota
            ? "Spotiguessr has hit today's free usage limit. Tell Kiril!"
            : "Couldn't load this song's audio.",
        });
      }
    })();

    return () => {
      cancelled = true;
      playerRef.current?.stop();
    };
  }, [trackId, attempt]);

  // Applied whenever it changes, so dragging the slider is audible immediately
  // rather than on the next Step.
  useEffect(() => {
    playerRef.current?.setVolume(volume);
  }, [volume, buffer]);

  /** Must run inside a user gesture — autoplay policy blocks a cold context. */
  const play = useCallback(
    async (durationSeconds: number, startSeconds = 0) => {
      if (!buffer || !playerRef.current) return;
      await playerRef.current.unlock();
      // Re-asserted here because the player may have been created after the last
      // volume effect ran.
      playerRef.current.setVolume(volume);
      playerRef.current.play(buffer, durationSeconds, startSeconds);
    },
    [buffer, volume],
  );

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { ready: buffer !== null, failure, play, retry };
}
